/**
 * `@immo/i18n` — single source of translated strings shared across the web
 * app (`next-intl` render path) and the backend communications + document
 * generators (`@formatjs/intl-messageformat` render path). Per ADR 0009.
 *
 * The locale enum is dictionary-driven: `Locale = keyof typeof dictionaries`,
 * so adding a value structurally requires a matching dictionary.
 *
 * Boundary rule (enforced by ESLint per ADR 0007 + ADR 0009): only
 * communications generators, document generators, and the web app may
 * import from this package. Command handlers, application services, domain
 * code, and HTTP error envelopes never import `@immo/i18n`.
 */

import { z } from 'zod';
import { RESERVED_ERROR_NAMESPACES, type ReservedErrorNamespace } from '@immo/api-contract';
import { dictionaries, type Locale, type Messages } from './dictionaries';

// The locale registry and `Messages`/`Locale` types live in the
// dependency-light `./dictionaries` module (no `@immo/api-contract`) so the
// i18n CI scripts can import the live locale set; re-export them here so the
// package's public surface is unchanged.
export { dictionaries, type Locale, type Messages };

export const LocaleSchema = z.enum(Object.keys(dictionaries) as [Locale, ...Locale[]]);

/**
 * The default fallback locale. Per ADR 0009 the runtime falls back to
 * `de-DE` when a key is unresolved in the requested locale, never to a raw
 * key string in front of a user.
 */
export const DEFAULT_LOCALE: Locale = 'de-DE';

export type { DeepWiden, EnumTranslation, ErrorMessages, Nested } from './types';

// How a permission reads on screen — label plus the literal the API docs
// specify. The catalogue lives here, so the format it is rendered in does too
// (IMM-620.1).
export { formatPermission, permissionLiteral } from './permission-format';

/**
 * Re-exported registry of reserved top-level keys under `errors.*` for
 * consumers that want to assert against it (the lint script, tests).
 */
export { RESERVED_ERROR_NAMESPACES, type ReservedErrorNamespace };
